import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// An electric arc furnace: a round steel furnace vessel on a tilting cradle, its
// roof pierced by three graphite electrodes lowered from a gantry, melting scrap
// with the arc. A molten glow shows at the shell seam; the furnace transformer
// sits alongside.
const SHELL = "#4a4a4e";
const SHELL_DK = "#3a3a3e";
const ROOF = "#5f656b";
const ELECTRODE = "#2a2a2e";
const CLAMP = "#b87333";
const GLOW = "#ff7a2a";
const GANTRY = "#6c727a";
const XFMR = "#878e96";

export const arcFurnace = defineModel({
  id: "arc-furnace",
  batchId: "electric",
  name: "Arc furnace",
  tags: ["building", "electric", "power", "industry", "furnace", "steel"],
  summary: "An electric arc furnace with three electrodes and a molten glow.",
  footprint: 0.8,
  parts(): Part[] {
    const parts: Part[] = [
      { geometry: new THREE.BoxGeometry(1.6, 0.12, 1.3), color: "#5a5a5e", position: [0, 0.06, 0] },
      // Tilting cradle rockers under the vessel.
      { geometry: new THREE.BoxGeometry(0.7, 0.1, 0.12), color: SHELL_DK, position: [-0.25, 0.16, -0.25] },
      { geometry: new THREE.BoxGeometry(0.7, 0.1, 0.12), color: SHELL_DK, position: [-0.25, 0.16, 0.25] },
      // Furnace shell with a glowing molten seam.
      { geometry: new THREE.CylinderGeometry(0.42, 0.38, 0.4, 16), color: SHELL, position: [-0.25, 0.42, 0] },
      { geometry: new THREE.CylinderGeometry(0.43, 0.43, 0.04, 16), color: GLOW, position: [-0.25, 0.36, 0] },
      { geometry: new THREE.CylinderGeometry(0.4, 0.42, 0.1, 16), color: SHELL_DK, position: [-0.25, 0.27, 0] },
      // Roof.
      { geometry: new THREE.CylinderGeometry(0.36, 0.42, 0.14, 16), color: ROOF, position: [-0.25, 0.69, 0] },
      // Three electrodes lowered through the roof, on a gantry.
      { geometry: new THREE.BoxGeometry(0.12, 1.1, 0.12), color: GANTRY, position: [-0.25, 0.7, -0.55] },
      { geometry: new THREE.BoxGeometry(0.12, 1.1, 0.12), color: GANTRY, position: [0.45, 0.7, -0.55] },
      { geometry: new THREE.BoxGeometry(0.9, 0.1, 0.12), color: GANTRY, position: [0.1, 1.2, -0.55] },
    ];
    const ex = [-0.45, -0.25, -0.05];
    for (const x of ex) {
      parts.push({ geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.7, 10), color: ELECTRODE, position: [x, 0.95, 0] });
      parts.push({ geometry: new THREE.BoxGeometry(0.1, 0.06, 0.08), color: CLAMP, position: [x, 1.1, -0.06] });
      parts.push({ geometry: new THREE.BoxGeometry(0.04, 0.04, 0.46), color: GANTRY, position: [x, 1.18, -0.3] });
    }
    // Furnace transformer alongside, with a duct over to the gantry.
    parts.push(
      { geometry: new THREE.BoxGeometry(0.4, 0.5, 0.36), color: XFMR, position: [0.62, 0.31, 0.2] },
      { geometry: new THREE.BoxGeometry(0.42, 0.05, 0.38), color: GANTRY, position: [0.62, 0.58, 0.2] },
      { geometry: new THREE.CylinderGeometry(0.04, 0.04, 0.4, 8), color: CLAMP, position: [0.4, 0.66, 0.1], rotation: [0, 0, Math.PI / 2.4] },
      // Fume hood duct rising at the back.
      { geometry: new THREE.CylinderGeometry(0.08, 0.08, 0.5, 10), color: SHELL_DK, position: [0.3, 0.95, -0.5] },
    );
    return parts;
  },
  animations: [],
});
